import { Button, Card, Checkbox, Col, Form, message, Row, Tabs } from "antd";
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { TagsInput } from "react-tag-input-component";
import cmaApi from "../../../../api/cmaApi";
import Ckeditor from "../../../../components/Ckeditor";
import InputField from "../../../../components/InputField";
import InputNumberWrap from "../../../../components/InputNumber";
import Loading from "../../../../components/Loading";
import SelectField from "../../../../components/SelectField";
import SeoOption from "../../../../components/SeoOption";
import TextAreaField from "../../../../components/TextAreaField";
import TopContent from "../../../../components/TopContent";
import PageWrap from "../../../../layout/PageWrap";
import { appRoutes } from "../../../../routes/routes";
import { formItemLayoutSm } from "../../../../utils/constants";
import { ConvertSlug } from "../../../../utils/functions/convertSlug";
import { loadFile } from "../../../../utils/functions/loadFile";
import { messages } from "../../../../utils/messages";

interface CmaArticleFormProps {
  type: number;
}

const CmaArticleForm: FC<CmaArticleFormProps> = ({ type }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const [seo, setSeo]: any = useState();
  const [file, setFile]: any = useState();
  const [image, setImage]: any = useState();
  const [tagVal, setTagVal]: any = useState([]);

  const { data, isLoading, isFetching }: any = useQuery(
    ["cma_articles", id],
    () => cmaApi.getArticleDetails(id),
    { enabled: type === 2 }
  );

  const { data: categories }: any = useQuery(
    "cma_article_categories",
    cmaApi.getCategories
  );

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (data?.data?.tag) {
      setTagVal(
        data.data.tag !== ""
          ? typeof data.data.tag === "string"
            ? data.data.tag.split(",")
            : data.data.tag
          : []
      );
    }
  }, [data]);

  const mutationEdit = useMutation(cmaApi.editArticle, {
    onSuccess: async (res: any) => {
      if (res.statusCode === 200) {
        message.success(messages.updateSuccess);
        await queryClient.invalidateQueries("cma_articles");
        navigate(appRoutes.CMA.ARTICLES.LIST);
        window.scrollTo(0, 0);
      } else message.error(res?.data?.message ?? messages.error);
    },
    onError: (err: any) => {
      message.error(err?.data?.message ?? messages.error);
    },
  });

  const mutationAdd = useMutation(cmaApi.addArticle, {
    onSuccess: async (res: any) => {
      if (res.statusCode === 200) {
        message.success(messages.addSuccess);
        await queryClient.invalidateQueries("cma_articles");
        navigate(appRoutes.CMA.ARTICLES.LIST);
        window.scrollTo(0, 0);
      } else message.error(res?.data?.message ?? messages.error);
    },
    onError: (err: any) => {
      message.error(err?.data?.message ?? messages.error);
    },
  });

  const handleUpload = async (f: any) => {
    if (f) {
      setFile(f);
      await loadFile(f);
      setImage(f.urlImg);
    }
  };

  const onSubmit = (dt: any) => {
    if (type === 1 && !file) {
      message.error("Vui lòng chọn hình ảnh.");
      return;
    }
    const formData = new FormData();
    if (file) formData.append("photo", file);
    formData.set("ten_vi", dt.ten_vi ?? "");
    formData.set("stt", dt.stt ?? 1);
    formData.set("slug_vi", dt.slug_vi ?? "");
    formData.set("mota_vi", dt.mota_vi ?? "");
    formData.set("noidung_vi", dt.noidung_vi ?? data?.data?.noidung_vi ?? "");
    formData.set(
      "noidung1_vi",
      dt.noidung1_vi ?? data?.data?.noidung1_vi ?? ""
    );
    formData.set(
      "noidung2_vi",
      dt.noidung2_vi ?? data?.data?.noidung2_vi ?? ""
    );
    formData.set(
      "noidung3_vi",
      dt.noidung3_vi ?? data?.data?.noidung3_vi ?? ""
    );
    formData.set(
      "noidung4_vi",
      dt.noidung4_vi ?? data?.data?.noidung4_vi ?? ""
    );
    formData.set(
      "noidung5_vi",
      dt.noidung5_vi ?? data?.data?.noidung5_vi ?? ""
    );
    formData.set(
      "noidung6_vi",
      dt.noidung6_vi ?? data?.data?.noidung6_vi ?? ""
    );
    formData.set(
      "noidung7_vi",
      dt.noidung7_vi ?? data?.data?.noidung7_vi ?? ""
    );
    const tagValue =
      dt.tag !== undefined
        ? Array.isArray(dt.tag)
          ? dt.tag.join(",")
          : dt.tag
        : Array.isArray(tagVal)
        ? tagVal.join(",")
        : data?.data?.tag ?? "";
    formData.set("tag", tagValue);
    formData.set("category", dt.category ?? data?.data?.category ?? "");
    formData.set("title_vi", seo?.title_vi ?? data?.data?.title_vi ?? "");
    formData.set(
      "keywords_vi",
      seo?.keywords_vi ?? data?.data?.keywords_vi ?? ""
    );
    formData.set(
      "description_vi",
      seo?.description_vi ?? data?.data?.description_vi ?? ""
    );
    if (type === 1) {
      formData.set("hienthi", dt.hienthi ?? false);
      formData.set("noibat", dt.noibat ?? false);
    }
    if (type === 2) {
      formData.set(
        "hienthi",
        dt.hienthi !== undefined ? dt.hienthi : data?.data?.hienthi ?? false
      );
      formData.set(
        "noibat",
        dt.noibat !== undefined ? dt.noibat : data?.data?.noibat ?? false
      );
    }
    if (type === 2) mutationEdit.mutate({ id, data: formData });
    if (type === 1) mutationAdd.mutate(formData);
  };

  const handleSeoSubmit = (seoData: any) => {
    setSeo(seoData);
    handleSubmit(onSubmit)();
  };

  const overview = (
    <>
      {isLoading || isFetching ? (
        <Loading />
      ) : (
        <Card bordered={false}>
          <Form {...formItemLayoutSm} className="ckeditor_form">
            {(image || (data?.data?.photo && type === 2)) && (
              <div className="cash_img">
                <img
                  src={
                    image ?? process.env.REACT_APP_DOMAIN + data?.data?.photo
                  }
                  alt=""
                />
              </div>
            )}
            <InputField
              isImage={true}
              name="photo"
              control={control}
              rules={
                type === 1
                  ? { required: "Vui lòng chọn hình ảnh" }
                  : undefined
              }
              label="Hình ảnh"
              errorMsg={errors?.photo?.message}
              onChange={(e: any) => {
                handleUpload(e.target.files?.[0]);
              }}
            />
            <InputField
              label="Tiêu đề"
              name="ten_vi"
              setValue={setValue}
              control={control}
              defaultValue={data?.data?.ten_vi}
              errorMsg={errors?.ten_vi?.message}
              rules={{ required: "Vui lòng nhập tiêu đề" }}
              onChange={(e: any) => {
                setValue("slug_vi", ConvertSlug(e.target.value));
              }}
            />
            <InputField
              label="Slug"
              name="slug_vi"
              control={control}
              setValue={setValue}
              defaultValue={data?.data?.slug_vi}
              errorMsg={errors?.slug_vi?.message}
              rules={{
                required: "Vui lòng nhập slug",
                minLength: {
                  value: 8,
                  message: "Vui lòng nhập trên 8 kí tự",
                },
              }}
            />
            <TextAreaField
              label="Mô tả"
              name="mota_vi"
              control={control}
              defaultValue={data?.data?.mota_vi}
              errorMsg={errors?.mota_vi?.message}
            />
            <SelectField
              placeholder="Danh mục"
              control={control}
              label="Danh mục"
              name="category"
              options={(categories?.data ?? []).map((e: any) => ({
                key: e._id,
                value: e.ten_vi,
              }))}
              defaultValue={data?.data?.category}
              errorMsg={errors?.category?.message}
              rules={{ required: "Vui lòng chọn danh mục" }}
            />
            <Form.Item className="ckeditor_form__item">
              <Ckeditor
                initData={data?.data?.noidung_vi}
                onChange={(evt: any) => {
                  setValue("noidung_vi", evt.editor.getData());
                }}
              />
              <Row
                gutter={[30, 23]}
                style={{ marginTop: "23px", marginBottom: "23px" }}
              >
                <Col lg={12} md={24} style={{ width: "100%" }}>
                  <Ckeditor
                    initData={data?.data?.noidung1_vi}
                    onChange={(evt: any) => {
                      setValue("noidung1_vi", evt.editor.getData());
                    }}
                  />
                </Col>
                <Col lg={12} md={24} style={{ width: "100%" }}>
                  <Ckeditor
                    initData={data?.data?.noidung2_vi}
                    onChange={(evt: any) => {
                      setValue("noidung2_vi", evt.editor.getData());
                    }}
                  />
                </Col>
              </Row>
              <Ckeditor
                initData={data?.data?.noidung3_vi}
                onChange={(evt: any) => {
                  setValue("noidung3_vi", evt.editor.getData());
                }}
              />
              <Row
                gutter={[30, 23]}
                style={{ marginTop: "23px", marginBottom: "23px" }}
              >
                <Col lg={12} md={24} style={{ width: "100%" }}>
                  <Ckeditor
                    initData={data?.data?.noidung4_vi}
                    onChange={(evt: any) => {
                      setValue("noidung4_vi", evt.editor.getData());
                    }}
                  />
                </Col>
                <Col lg={12} md={24} style={{ width: "100%" }}>
                  <Ckeditor
                    initData={data?.data?.noidung5_vi}
                    onChange={(evt: any) => {
                      setValue("noidung5_vi", evt.editor.getData());
                    }}
                  />
                </Col>
              </Row>
              <div style={{ marginBottom: "23px" }}>
                <Ckeditor
                  initData={data?.data?.noidung6_vi}
                  onChange={(evt: any) => {
                    setValue("noidung6_vi", evt.editor.getData());
                  }}
                />
              </div>
              <Ckeditor
                initData={data?.data?.noidung7_vi}
                onChange={(evt: any) => {
                  setValue("noidung7_vi", evt.editor.getData());
                }}
              />
            </Form.Item>
            <Form.Item
              label="Tag"
              colon={false}
              style={{ marginTop: "26px" }}
            >
              <TagsInput
                value={tagVal}
                onChange={(e: any) => {
                  setValue("tag", e);
                  setTagVal(e);
                }}
                onKeyUp={(e: any) => {
                  if (e?.key === "Enter") {
                    e.target.value = "";
                  }
                }}
              />
            </Form.Item>
            <InputNumberWrap
              label="STT"
              name="stt"
              control={control}
              defaultValue={data?.data?.stt ?? 1}
              onBlur={(e: any) => {
                if (e.target.value === "") setValue("stt", 1);
              }}
              min={1}
            />
            <Form.Item className="checkbox">
              <Row gutter={[20, 5]}>
                <Col>
                  <Checkbox
                    defaultChecked={data?.data?.noibat}
                    name="noibat"
                    onClick={(e: any) => {
                      setValue("noibat", e.target.checked);
                    }}
                  />
                  &nbsp;Nổi bật
                </Col>
                <Col>
                  <Checkbox
                    defaultChecked={data?.data?.hienthi}
                    name="hienthi"
                    onClick={(e: any) => {
                      setValue("hienthi", e.target.checked);
                    }}
                  />
                  &nbsp;Hiển thị
                </Col>
              </Row>
            </Form.Item>
            <Row justify="start" gutter={[10, 0]}>
              <Col>
                <Button
                  type="primary"
                  danger
                  onClick={handleSubmit(onSubmit)}
                >
                  Lưu
                </Button>
              </Col>
              <Col>
                <Button
                  onClick={() => {
                    window.scrollTo(0, 0);
                    navigate(appRoutes.CMA.ARTICLES.LIST);
                  }}
                >
                  Hủy
                </Button>
              </Col>
            </Row>
          </Form>
        </Card>
      )}
    </>
  );

  return (
    <PageWrap
      title="CMA · Bài viết"
      content={
        <>
          <TopContent
            content={
              <Row>
                <Col>
                  <b>CMA · Bài viết</b>
                </Col>
              </Row>
            }
          />
          <div className="content">
            <Tabs
              type="card"
              items={[
                { label: "Tổng quan", key: "0", children: overview },
                {
                  label: "SEO option",
                  key: "1",
                  children: (
                    <SeoOption
                      keyQuery={["cma_articles_seo", id]}
                      query={() =>
                        type === 2
                          ? cmaApi.getArticleDetails(id)
                          : Promise.resolve({ data: {} })
                      }
                      link={appRoutes.CMA.ARTICLES.LIST}
                      onSubmit={handleSeoSubmit}
                      setSeo={(e: any) => setSeo(e)}
                    />
                  ),
                },
              ]}
            />
          </div>
        </>
      }
    />
  );
};

export default CmaArticleForm;
